import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { School } from '@awo-care/shared-core';
import {
  filterSchools,
  groupSchoolsByKreis,
  isSchool,
  schoolDisplayName,
} from './school-autocomplete.utils';

/** Wert der Zusatz-Option „keine bestimmte Schule“ — landet nie im Formular. */
const GENERAL_OPTION = '__general__';

/**
 * Suchfeld für die Schulauswahl (#1145): Eltern tippen Schulname oder Ort
 * statt durch eine lange Klappliste zu scrollen. Das Control hält erst dann
 * ein `School`-Objekt, wenn ein Treffer gewählt wurde — Validierung dazu
 * liefert `schoolSelectedValidator()`.
 */
@Component({
  selector: 'lib-school-autocomplete',
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  template: `
    <mat-form-field appearance="outline" class="school-autocomplete" color="accent">
      <mat-label>{{ label() }}</mat-label>
      <mat-icon matPrefix aria-hidden="true">search</mat-icon>
      <input
        matInput
        type="text"
        autocomplete="off"
        [formControl]="control()"
        [matAutocomplete]="auto"
        [placeholder]="placeholder"
        [attr.data-testid]="testId()"
        (input)="onInput($event)"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        [displayWith]="displayWith"
        (optionSelected)="onSelected($event)"
      >
        @for (group of groups(); track group.kreis) {
          <mat-optgroup [label]="group.kreis">
            @for (school of group.schools; track school.id) {
              <mat-option [value]="school" [attr.data-testid]="testId() + '-option'">
                <span class="school-name">{{ school.schoolName }}</span>
                @if (school.address?.city) {
                  <span class="school-city">{{ school.address.city }}</span>
                }
              </mat-option>
            }
          </mat-optgroup>
        } @empty {
          <mat-option disabled i18n="@@lib.school-autocomplete.noMatch">
            Keine Schule gefunden
          </mat-option>
        }
        @if (generalOptionLabel(); as generalLabel) {
          <mat-option
            [value]="generalOption"
            class="general-option"
            [attr.data-testid]="testId() + '-general'"
          >
            {{ generalLabel }}
          </mat-option>
        }
      </mat-autocomplete>
      @if (control().hasError('schoolNotSelected')) {
        <mat-error i18n="@@lib.school-autocomplete.notSelected"
          >Bitte wählen Sie eine Schule aus der Liste aus.</mat-error
        >
      } @else if (control().hasError('required')) {
        <mat-error>{{ requiredMessage() }}</mat-error>
      } @else if (hint()) {
        <mat-hint>{{ hint() }}</mat-hint>
      }
    </mat-form-field>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .school-autocomplete {
        width: 100%;
      }
      mat-icon[matPrefix] {
        margin: 0 4px 0 8px;
        color: rgba(0, 0, 0, 0.54);
      }
      .school-city {
        margin-left: 8px;
        font-size: 0.8125rem;
        color: rgba(0, 0, 0, 0.54);
      }
      .general-option {
        border-top: 1px solid rgba(0, 0, 0, 0.12);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SchoolAutocompleteComponent {
  readonly control = input.required<FormControl<School | string | null>>();
  readonly schools = input<School[]>([]);
  readonly label = input($localize`:@@lib.school-autocomplete.label:Schule`);
  readonly hint = input('');
  readonly requiredMessage = input(
    $localize`:@@lib.school-autocomplete.required:Bitte wählen Sie eine Schule aus.`
  );
  /** Ist sie gesetzt, steht am Ende der Liste eine Option ohne Schule. */
  readonly generalOptionLabel = input<string | null>(null);
  readonly testId = input('school-autocomplete');

  /** Die Option „keine bestimmte Schule“ wurde gewählt. */
  readonly generalSelected = output<void>();

  readonly generalOption = GENERAL_OPTION;
  readonly placeholder = $localize`:@@lib.school-autocomplete.placeholder:Schulname oder Ort eingeben`;

  private readonly query = signal('');

  readonly groups = computed(() =>
    groupSchoolsByKreis(filterSchools(this.schools(), this.query()))
  );

  readonly displayWith = (value: School | string | null): string => {
    if (isSchool(value)) {
      return schoolDisplayName(value);
    }
    return value === GENERAL_OPTION ? '' : value ?? '';
  };

  onInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    this.query.set('');
    if (event.option.value === GENERAL_OPTION) {
      this.control().setValue(null);
      this.generalSelected.emit();
    }
  }
}
